import { useState } from "react";
import Button from "@/components/common/form/button";
import Input from "@/components/common/form/input";
import Select from "@/components/common/form/select";
import Form from "@/components/common/form/form";
import { FaPlus, FaRegTrashAlt } from "react-icons/fa";
import SimpleRadio from "@/components/common/simple-form/simple-radio";
import { v4 as uuidv4 } from "uuid";
import { useDispatch } from "react-redux";
import { fertigatorServices } from "services/fertigator.services";
import { useTranslation } from "react-i18next";
import { authServices } from "services/auth.services";
import { setValveData } from "@/redux/actions/valves/valveData";
import { useSwrGet } from "@/utils/use-swr-get";
import LocationModal from "./locationModal";
import EditLocationModal from "./editLocationModal";

function SelectField({ t, passProps, ...props }) {
  return (
    <Select
      label={t("Select Field")}
      name="object_id"
      options={
        props.getValues().farm_id
          ? props.farmData
              ?.filter?.((farm) => farm.farm_id == props.getValues().farm_id)
              ?.map?.((item) => item.fields)[0]
              ?.map?.((item) => ({
                label: item.field_name,
                value: item.field_id,
              }))
          : []
      }
      {...props}
    />
  );
}
function SubmitButton({ t, passProps, ...props }) {
  return (
    <Button
      title={t("Save")}
      loading={props?.formState?.isSubmitting ?? false}
      className="w-full h-[35px] text-white rounded-lg bg-main  text-md mt-5"
    />
  );
}
const FertigatorForm = ({
  setShowModalFertigators,
  rowData = {},
  defaultValues,
  icon = "",
}) => {
  const {
    data: farmData,
    isLoading: farmIsLoading,
    isError: farmIsError,
  } = useSwrGet(
    `/farm/all`,
    process.env.NEXT_PUBLIC_BASE_URL +
      `farms/details/${authServices?.userValue?.org_id}/`
  );
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showLocationModal, setLocationModal] = useState(false);
  const [showEditLocationModal, setEditLocationModal] = useState(false);

  const onSubmit = async (
    data,
    e,
    reset: (
      values?: any,
      keepStateOptions?: Partial<{
        keepErrors: boolean;
        keepDirty: boolean;
        keepValues: boolean;
        keepDefaultValues: boolean;
        keepIsSubmitted: boolean;
        keepTouched: boolean;
        keepIsValid: boolean;
        keepSubmitCount: boolean;
      }>
    ) => void
  ) => {
    let res = null;
    if (icon) {
      res = await fertigatorServices.createFertigator({
        ...data,
        creation_date: "2021-10-10",
        origin_country: "EG",
        org_id: authServices?.userValue?.org_id ?? "",
        entity_type_id: 26,
        fertigator_code: "FRG-" + data.fertigator_code,
        content_type: farmData
          ?.filter?.((item) => item.farm_id == data.farm_id)
          ?.map?.((item) =>
            item.farm_type === "tree"
              ? authServices.userValue.content_types
                  ?.filter?.((item) => item.model == "treefieldinfo")
                  ?.find?.((item) => item.id)?.id
              : authServices.userValue.content_types
                  ?.filter?.((item) => item.model == "pivotfieldinfo")
                  ?.find?.((item) => item.id)?.id
          )[0],
      });
    } else {
      res = await fertigatorServices.updateFertigator(
        {
          ...data,
          creation_date: "2021-10-10",
          origin_country: "EG",
          org_id: authServices?.userValue?.org_id ?? "",
          entity_type_id: 26,
          content_type: farmData
            ?.filter((item) => item.farm_id == data.farm_id)
            .map?.((item) =>
              item.farm_type === "tree"
                ? authServices.userValue.content_types
                    ?.filter?.((item) => item.model == "treefieldinfo")
                    ?.find?.((item) => item.id)?.id
                : authServices.userValue.content_types
                    ?.filter?.((item) => item.model == "pivotfieldinfo")
                    ?.find?.((item) => item.id)?.id
            )[0],
        },
        defaultValues?.id
      );
    }
    if (res) {
      dispatch(
        setValveData({
          id: res.data.id,
          entity_type_id: res.data.entity_type_id,
        })
      );
      if (icon) {
        setLocationModal(true);
      } else {
        setEditLocationModal(true);
      }
    }
  };
  return (
    <>
      <div className="relative min-h-full overflow-auto form-wrap form-area">
        <div className="w-full max-w-full ">
          <Form onSubmit={onSubmit} defaultValues={defaultValues}>
            <div className="flex flex-wrap items-start justify-start w-full form-wrap ">
              <div className="px-6 pt-6 w-80 lg:w-96">
                <Select
                  label={t("Select Farm")}
                  name="farm_id"
                  options={farmData?.map?.((farm) => ({
                    label: farm.farm_name,
                    value: farm.farm_id,
                  }))}
                />
                <SelectField t={t} farmData={farmData} passProps />
                <Select
                  name="fertigator_type"
                  label={t("Fertigator Type")}
                  options={[
                    { label: t("Movable"), value: "movable" },
                    { label: t("Stationary"), value: "stationary " },
                  ]}
                />
                <Input
                  name="fertigator_code"
                  label={t("Fertigator Code")}
                  icon={() => (
                    <p className="px-2 pt-4 text-sm font-bold outline-none appearance-none text-mako">
                      {icon}
                    </p>
                  )}
                />

                <Select
                  label={t("Fertigator Technique")}
                  name="fertigator_technique"
                  options={[
                    { label: t("Motor"), value: "motor" },
                    { label: t("Pressure"), value: "pressure" },
                  ]}
                />
              </div>
            </div>
            <Motor passProps />

            <SubmitButton passProps t={t} />
          </Form>
        </div>
      </div>
      <LocationModal
        showModal={showLocationModal}
        setShowModal={setLocationModal}
        closeMainModal={setShowModalFertigators}
      />
      {(Object?.keys?.(rowData ?? {})?.length > 0 || icon == "") && (
        <EditLocationModal
          showModal={showEditLocationModal}
          setShowModal={setEditLocationModal}
          closeMainModal={setShowModalFertigators}
          rowData={rowData}
        />
      )}
    </>
  );
};
function Motor({ passProps, ...props }) {
  const [mixingMotor, setMixingMotor] = useState(false);
  const { t, i18n } = useTranslation();
  const [injectionMotor, setInjectionMotor] = useState(false);
  const [counter, setCounter] = useState(0);
  return (
    <div>
      {props.getValues("fertigator_technique") && (
        <div className="flex flex-col px-6 full">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-between w-4/12 gap-2">
              <div className="flex items-center h-20">
                <SimpleRadio
                  label={t("Mixing motor")}
                  value={
                    mixingMotor ||
                    (props
                      ?.getValues?.("attributes_value.motors")
                      ?.filter?.((item) => item.type === "Mixing")?.length >
                      0 ??
                      false)
                  }
                  setValue={setMixingMotor}
                />
              </div>
              {mixingMotor && (
                <div
                  className="flex items-center justify-center p-2 border-2 rounded-full cursor-pointer text-light-black border-light-black"
                  onClick={(e) => {
                    setCounter(counter + 1);
                    let uuid = uuidv4();
                    props.setValue(
                      "attributes_value.motors",
                      props.getValues("attributes_value.motors")
                        ? [
                            ...props.getValues("attributes_value.motors"),
                            {
                              uuid: uuid,
                              name: `Mixing Motor ${
                                props.getValues("attributes_value.motors")
                                  ?.length + 1
                              } Power`,
                              type: "Mixing",
                              power: "",
                            },
                          ]
                        : [
                            {
                              uuid: uuid,
                              name: `Mixing Motor ${1} Power`,
                              type: "Mixing",
                              power: "",
                            },
                          ]
                    );
                    props.setValue(
                      "attributes_value.fertigator_details",
                      props.getValues("attributes_value.fertigator_details")
                        ? [
                            ...props.getValues(
                              "attributes_value.fertigator_details"
                            ),
                          ]
                        : []
                    );
                  }}
                >
                  <FaPlus />
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center w-9/12">
              {props.getValues("attributes_value.motors")?.map(
                (motor, i) =>
                  motor.type === "Mixing" && (
                    <div key={i + Math.random()} className="w-7/12 pr-2">
                      <div className="relative flex items-center w-full">
                        <Input
                          name={`attributes_value.motors.${i}.power`}
                          label={motor.name}
                          labelClassName="text-xs"
                          {...props}
                        />
                        <div
                          onClick={(e) => {
                            setCounter(counter + 1);
                            props.setValue(
                              "attributes_value.motors",
                              props
                                .getValues("attributes_value.motors")
                                .filter((mo, m) => mo.uuid !== motor.uuid)
                            );
                            props.setValue(
                              "attributes_value.fertigator_details",
                              props
                                .getValues(
                                  "attributes_value.fertigator_details"
                                )
                                .filter(
                                  (fertigator, m) =>
                                    fertigator.motor_uuid !== motor.uuid
                                )
                            );
                          }}
                          className="absolute right-0 flex items-center justify-center h-12 p-3 text-white rounded-md bg-old-rose"
                        >
                          <FaRegTrashAlt />
                        </div>
                      </div>
                    </div>
                  )
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center justify-between w-4/12 gap-2">
              <div className="flex items-center h-20">
                <SimpleRadio
                  label={t("Injection motor")}
                  value={
                    injectionMotor ||
                    (props
                      ?.getValues?.("attributes_value.motors")
                      ?.filter?.((item) => item.type === "injection")?.length >
                      0 ??
                      false)
                  }
                  setValue={setInjectionMotor}
                />{" "}
              </div>
              {injectionMotor && (
                <div
                  className="flex items-center justify-center p-2 border-2 rounded-full cursor-pointer text-light-black border-light-black"
                  onClick={(e) => {
                    let uuid = uuidv4();
                    setCounter(counter + 1);
                    props.setValue(
                      "attributes_value.motors",
                      props.getValues("attributes_value.motors")
                        ? [
                            ...props.getValues("attributes_value.motors"),
                            {
                              uuid: uuid,
                              name: `Injection Motor ${
                                props.getValues("attributes_value.motors")
                                  ?.length + 1
                              } Power`,
                              type: "injection",
                              power: "",
                            },
                          ]
                        : [
                            {
                              uuid: uuid,
                              name: `Injection Motor ${1} Power`,
                              type: "injection",
                              power: "",
                            },
                          ]
                    );
                    props.setValue(
                      "attributes_value.fertigator_details",
                      props.getValues("attributes_value.fertigator_details")
                        ? [
                            ...props.getValues(
                              "attributes_value.fertigator_details"
                            ),
                            // {
                            //   uuid: uuidv4(),
                            //   motor_uuid: [uuid],
                            //   tank_code: "TNK-",
                            //   tank_capacity: "0",
                            // },
                          ]
                        : [
                            // {
                            //   uuid: uuidv4(),
                            //   motor_uuid: [uuid],
                            //   tank_code: "TNK-",
                            //   tank_capacity: "0",
                            // },
                          ]
                    );
                  }}
                >
                  <FaPlus />
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center w-9/12">
              {props.getValues("attributes_value.motors")?.map(
                (motor, i) =>
                  motor.type === "injection" && (
                    <div key={i + Math.random()} className="w-7/12 pr-2">
                      <div className="relative flex items-center w-full">
                        <Input
                          name={`attributes_value.motors.${i}.power`}
                          label={motor.name}
                          labelClassName="text-xs"
                          {...props}
                        />
                        <div
                          onClick={(e) => {
                            setCounter(counter + 1);
                            props.setValue(
                              "attributes_value.motors",
                              props
                                .getValues("attributes_value.motors")
                                .filter((mo, m) => mo.uuid !== motor.uuid)
                            );
                            props.setValue(
                              "attributes_value.fertigator_details",
                              props
                                .getValues(
                                  "attributes_value.fertigator_details"
                                )
                                .filter(
                                  (fertigator, m) =>
                                    fertigator.motor_uuid !== motor.uuid
                                )
                            );
                          }}
                          className="absolute right-0 flex items-center justify-center h-12 p-3 text-white rounded-md bg-old-rose"
                        >
                          <FaRegTrashAlt />
                        </div>
                      </div>
                    </div>
                  )
              )}
            </div>
          </div>

          {props.getValues(`attributes_value.motors`)?.length > 0 &&
            props.getValues("attributes_value.fertigator_details")?.length ===
              0 && (
              <div className="flex items-center gap-2">
                <div
                  className="flex items-center justify-center p-2 border-2 rounded-full w-9 h-9 text-light-black border-light-black"
                  onClick={() => {
                    setCounter(counter + 1);
                    let uuid = uuidv4();

                    props.setValue(
                      "attributes_value.fertigator_details",
                      props.getValues("attributes_value.fertigator_details")
                        ? [
                            ...props.getValues(
                              "attributes_value.fertigator_details"
                            ),
                            {
                              uuid: uuid,
                              motor_uuid: [],
                              tank_code: "TNK-",
                              tank_capacity: "0",
                            },
                          ]
                        : [
                            {
                              uuid: uuid,
                              motor_uuid: [],
                              tank_code: "TNK-",
                              tank_capacity: "0",
                            },
                          ]
                    );
                  }}
                >
                  <FaPlus />
                </div>
                <div>{t("Add tank")}</div>
              </div>
            )}
          <div className="flex flex-col items-start w-full gap-2 my-6">
            {props
              .getValues("attributes_value.fertigator_details")
              ?.map((fertigator, i) => (
                <div
                  key={i + Math.random()}
                  className="flex items-center w-full gap-2"
                >
                  <Input
                    label={`Tank ${i + 1} Code`}
                    name={`attributes_value.fertigator_details.${i}.tank_code`}
                    {...props}
                  />
                  <Input
                    label={`Tank ${i + 1} Capacity Liters`}
                    name={`attributes_value.fertigator_details.${i}.tank_capacity`}
                    {...props}
                  />
                  <Select
                    label={`Tank ${i + 1} Mixing Motor`}
                    name={`attributes_value.fertigator_details.${i}.motor_uuid`}
                    options={props
                      .getValues(`attributes_value.motors`)
                      .filter((motor) => motor.type === "Mixing")
                      .map((motor) => {
                        return {
                          value: motor.uuid,
                          label: motor.name,
                        };
                      })}
                    {...props}
                    textSize="text-sm"
                  />
                  <Select
                    label={`Tank ${i + 1} Injection Motor`}
                    name={`attributes_value.fertigator_details.${i}.motor_uuid`}
                    options={props
                      .getValues(`attributes_value.motors`)
                      .filter((motor) => motor.type === "injection")
                      .map((motor) => {
                        return {
                          value: motor.uuid,
                          label: motor.name,
                        };
                      })}
                    {...props}
                    textSize="text-sm"
                  />
                  <div className="flex items-center w-full gap-x-4">
                    {/* <Image src={deleteIcon} alt="delete" /> */}
                    <div
                      className="flex items-center justify-center p-2 text-white rounded-full bg-old-rose"
                      onClick={() => {
                        setCounter(counter + 1);
                        props.setValue(
                          "attributes_value.fertigator_details",
                          props
                            .getValues("attributes_value.fertigator_details")
                            .filter((f) => f.uuid !== fertigator.uuid)
                        );
                      }}
                    >
                      <FaRegTrashAlt />
                    </div>
                    {(props.getValues(`attributes_value.motors`)?.length ===
                      i + 1 ||
                      props.getValues(`attributes_value.fertigator_details`)
                        ?.length === 1) && (
                      <div
                        className="flex items-center justify-center p-2 border-2 rounded-full text-light-black border-light-black"
                        onClick={() => {
                          setCounter(counter + 1);
                          let uuid = uuidv4();

                          props.setValue(
                            "attributes_value.fertigator_details",
                            props.getValues(
                              "attributes_value.fertigator_details"
                            )
                              ? [
                                  ...props.getValues(
                                    "attributes_value.fertigator_details"
                                  ),
                                  {
                                    uuid: uuid,
                                    motor_uuid: [],
                                    tank_code: "TNK-",
                                    tank_capacity: "0",
                                  },
                                ]
                              : [
                                  {
                                    uuid: uuid,
                                    motor_uuid: [],
                                    tank_code: "TNK-",
                                    tank_capacity: "0",
                                  },
                                ]
                          );
                        }}
                      >
                        <FaPlus />
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default FertigatorForm;
